import { useMemo, useRef, useState } from "react";
import {
  AuditSession,
  CheckResult,
  type ColumnType,
  SourceRow,
} from "../../../../models/audit/auditSession";
import { AlertTriangle, RefreshCw, AlertCircle, Sparkles } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../../../../components/ui/context-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { toast } from "sonner";
import { ApiError, fetchFastApi } from "../../../../api/fastapi";
import { useAudit } from "../../../../contexts/auditContext";
import { useHookCache } from "../../../../lib/hookCache";
import {
  useSessionRunsContext,
  createPlaceholderSessionRun,
  getHighlightCellClassName,
  HighlightEdges,
} from "../../../../contexts/sessionRunsContext";
import { getIssueDisplayStatus } from "../utils/issueDisplayStatus";
import { CellErrorTooltipContent } from "../../../../components/audit/CellErrorTooltipContent";
import { RegenerateWithInstructionsDialog } from "./RegenerateWithInstructionsDialog";
import { AttributeTestConclusionCard } from "./AttributeTestConclusionCard";
import { AnalyticsConclusionCard } from "./AnalyticsConclusionCard";
import { analyticsResultIdFromSelection } from "../../../../models/audit/analyticsTranslator";
import { type VerdictConclusionFields } from "../lib/verdictOrigin";
import { CellLockButton } from "./CellLockButton";
import {
  runUnbuiltAnalyticsColumnAuthoringOffCopy,
  useHybridTestingAuthoring,
} from "../../../../hooks/useHybridTestingAvailability";
import { useSessionAnalyticsOwner } from "../../../../hooks/audit/useSessionAnalyticsOwner";

const DEFAULT_COLUMN_WIDTH = 200;

/**
 * Dry/heavy source-row shape accepted by `CheckDecision`. Only `.id` is
 * read; passing the dry envelope (`tableShell.sourceRows[*]`) is fine.
 */
type SourceRowLike = { id: string };

/**
 * Which kind of column this cell renders, and what that kind brings with it.
 * The kind is read at exactly one site — the memo that resolves the conclusion
 * affordance — so a further kind lands there without a second boolean beside
 * the first.
 *
 * It is deliberately not read by `getStatusClasses` or `getStatusDisplay`.
 * Those switch on `displayStatus` alone, and their vocabulary is shared by
 * every kind: a `fail` that looked different because of which engine produced
 * it would make the colour ambiguous on all of them while encoding a fact the
 * column header already states once.
 *
 * The evaluated cell omits the kind: it has no conclusion affordance and never
 * needed to name itself.
 */
type ColumnKindProps =
  | {
      columnKind?: Exclude<ColumnType, "analytics">;
      analyticsVerdictProvenance?: never;
    }
  | {
      columnKind: "analytics";
      /**
       * The analytics verdict's provenance, off the summary the analytics cell
       * already holds. Its conclusion affordance reads who concluded the
       * verdict from this until its detail fetch settles, then from the fetched
       * verdict, so whether Midship or an auditor concluded it shows at once;
       * another auditor's name follows the user lookup.
       *
       * Required rather than optional so an analytics caller cannot drop it and
       * silently lose the affordance; `undefined` is the caller withholding a
       * verdict it cannot resolve, which renders no affordance on purpose.
       */
      analyticsVerdictProvenance: VerdictConclusionFields | undefined;
    };

type CheckDecisionProps = CheckDecisionSharedProps & ColumnKindProps;

interface CheckDecisionSharedProps {
  onCellSelection: ({
    sourceRowId,
    documentId,
    attributeId,
    checkResultId,
    supportColumnId,
    value,
    evalColumnId,
  }: {
    sourceRowId?: string;
    documentId?: string;
    attributeId?: string;
    checkResultId?: string;
    supportColumnId?: string;
    value?: string;
    evalColumnId?: string;
  }) => void;
  /**
   * Pre-resolved by the table parent. Phase 3: the parent does the heavy
   * single-row fetch via `useSourceRow` and runs `getLatestCheckResult`
   * (or translates an attribute-test result) before passing the cell
   * down. `CheckDecision` itself never touches `sourceRow.checkResults`.
   */
  checkResult?: CheckResult;
  sourceRow: SourceRowLike;
  column: AuditSession["procedure"]["columns"][number];
  currentSelection?: {
    sourceRowId?: string;
    documentId?: string;
    attributeId?: string;
    value?: string;
    checkResultId?: string;
  };
  width?: number;
  isLast?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  isTimeout?: boolean;
  errorMessage?: string;
  /**
   * The failure is scoped to this column: the run settled rather than failing
   * wholesale, and `columnErrors` names this column. One run can name several
   * columns, so this says nothing about whether the row's other columns
   * generated. Rendered in the warning vocabulary rather than the error one,
   * because neither the sample nor the test failed — one cell has no result.
   */
  isColumnFailure?: boolean;
  /**
   * A prior verdict is still on screen beneath this column failure, so the lead
   * names a failed regeneration over an earlier answer rather than an absent
   * result. Copy only — the amber column-failure styling is unaffected. Analytics
   * cells set it; the other kinds do not carry a verdict past a column failure
   * and leave it unset.
   */
  priorResultVisible?: boolean;
  /**
   * Replaces the sentence the tooltip leads with, for a caller whose failure
   * neither the run lead nor the column lead describes. Omitted by every other
   * cell kind, so their copy is what it was.
   */
  errorLead?: string;
  /**
   * Replaces the word the empty cell shows under a column failure, for a caller
   * whose failure "Could Not Generate" misstates — a cell that generated more
   * than once, or one whose verdict exists but cannot be read. Omitted by every
   * other cell kind, so their label is what it was.
   */
  errorLabel?: string;
  isHighlighted?: boolean;
  // Per-side border sides for the highlighted-region merge — omitted call
  // sites fall back to a full standalone border via
  // `getHighlightCellClassName`'s default.
  highlightEdges?: HighlightEdges;
  auditSessionId: string;
  /**
   * Whether this cell's verdict can be regenerated under auditor-supplied
   * instructions. An analytics verdict comes from a workflow run rather than a
   * prompt, and nothing carries instructions into that run, so the cell clears
   * this and the menu drops the item instead of promising a regeneration that
   * cannot happen. Left set by every other cell kind, which keeps the item and
   * the plain re-run alike.
   */
  canRerunWithInstructions?: boolean;
  // MS-2533: grid lock toggle. The parent builds the full closure (it knows
  // whether this is an eval or attribute-test result and owns the optimistic
  // cache flip); absent when the cell has no result to lock.
  onToggleLock?: () => void;
  // Notifies the parent that a rerun-with-instructions just started for this
  // cell, so it can keep the "Running" badge lit through the persist-to-control
  // phase (where no server run exists yet). Parent owns the placeholder run;
  // the returned cleanup removes it early if the request fails.
  onRerunPending?: (
    sourceRowId: string,
    targetColumnIds: string[] | null,
  ) => () => void;
}

export const CheckDecision = ({
  onCellSelection,
  checkResult,
  sourceRow,
  column,
  currentSelection,
  width,
  isLast,
  isLoading,
  hasError,
  isTimeout = false,
  errorMessage,
  isColumnFailure = false,
  priorResultVisible = false,
  errorLead,
  errorLabel,
  isHighlighted = false,
  highlightEdges,
  auditSessionId,
  // No default initializer: one would stop TypeScript narrowing the provenance
  // off the kind in the conclusion memo.
  columnKind,
  analyticsVerdictProvenance,
  canRerunWithInstructions = true,
  onToggleLock,
  onRerunPending,
}: CheckDecisionProps) => {
  // Phase 3: `controlSession` reads here are envelope-only (status check
  // for "complete" gating). The heavy `useProcedureSession` call that
  // used to live in this component is GONE, so we no longer fan out the
  // heavy per-procedure megaquery from every cell on the table.
  const { controlSession } = useAudit();
  const analyticsOwner = useSessionAnalyticsOwner();
  const { authoringOff: analyticsAuthoringOff } = useHybridTestingAuthoring(
    analyticsOwner?.kind,
  );
  const { setEntry } = useHookCache();
  const { addSessionRun, isHighlightFading } = useSessionRunsContext();
  const cellRef = useRef<HTMLDivElement>(null);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);

  const isSelected = useMemo(() => {
    return (
      currentSelection?.checkResultId === checkResult?.id &&
      currentSelection?.sourceRowId === sourceRow.id
    );
  }, [currentSelection, checkResult?.id, sourceRow.id]);

  const isStale = useMemo(() => {
    // Locked results are deliberately pinned — the padlock supersedes the ⚠
    // (a rerun can't refresh them anyway). Human results keep their
    // pre-existing exemption.
    if (
      !checkResult ||
      isLoading ||
      checkResult.reviewerType == "human" ||
      checkResult.lockedBy
    )
      return false;
    return checkResult.columnVersion !== column.columnVersion;
  }, [checkResult, column.columnVersion, isLoading]);

  // The `column` prop is the procedure column this cell renders, so its id is
  // the rerun target the API expects in `target_column_ids` — the same id the
  // support-column cell (`DataCell`) sends. Reading it from the column rather
  // than from the result is what lets a cell with no result at all (a run
  // that failed before ever producing one) still be re-run.
  const procedureColumnId = column.id;

  // The conclusion affordance the cell's kind calls for, or none. Each kind
  // reads its own id namespace through that namespace's own reader: an
  // analytics verdict id and an attribute-test result id are drawn from
  // different tables and fetched from different endpoints, so reusing one
  // prefix for the other makes the detail hook ask the wrong one.
  const conclusionCard = useMemo(() => {
    if (columnKind === "attribute_test") {
      const prefix = "attr-test-id-";
      if (!checkResult?.id?.startsWith(prefix)) return null;
      return (
        <AttributeTestConclusionCard
          procedureSessionId={auditSessionId}
          attributeTestResultId={checkResult.id.substring(prefix.length)}
        />
      );
    }

    if (columnKind === "analytics") {
      const analyticsResultId = analyticsResultIdFromSelection(checkResult?.id);
      if (!analyticsResultId || !analyticsVerdictProvenance) return null;
      return (
        <AnalyticsConclusionCard
          procedureSessionId={auditSessionId}
          analyticsResultId={analyticsResultId}
          provenance={analyticsVerdictProvenance}
        />
      );
    }

    return null;
  }, [columnKind, checkResult?.id, auditSessionId, analyticsVerdictProvenance]);

  const isRerunDisabled =
    isLoading ||
    !!checkResult?.lockedBy ||
    controlSession?.status === "complete";

  const isColumnScopedFailure = Boolean(hasError && isColumnFailure);

  // A run failure and a column failure lead with different sentences because
  // they are different facts: the run failed wholesale, so nothing on the row
  // generated, or it settled and `columnErrors` names this column. One run can
  // name several columns, so the column lead says nothing about whether the
  // row's other columns generated. A column failure that left an earlier verdict
  // on screen leads instead with the failed regeneration, so the alert does not
  // read as an absence over a visible result. The backend's own sentence follows
  // either as the reason.
  //
  // A caller carrying its own lead names a failure neither channel describes —
  // the column produced a result and this cell cannot show it — so its sentence
  // replaces the lead rather than qualifying it. The reason still follows.
  // Lead-only: CellErrorTooltipContent appends the backend reason (the run id
  // stays in the Run Log popover). A caller-supplied lead wins; a column-scoped failure names its own
  // channel; everything else falls through to the shared timeout / generic lead
  // (returned as undefined so the shared component decides).
  const computedErrorLead =
    errorLead ??
    (isColumnScopedFailure
      ? priorResultVisible
        ? "This column failed to regenerate; the result shown is from an earlier run."
        : "This column produced no result for this sample."
      : undefined);

  const handleRerunEval = async () => {
    if (!controlSession) {
      toast.error("Missing information to re-run decision");
      return;
    }
    if (checkResult?.lockedBy) {
      toast.info("Cell is locked");
      return;
    }

    // No placeholder means the column is an unbuilt analytics column, which the
    // server answers with a no-op, so nothing is marked as running for it.
    const sessionRun = createPlaceholderSessionRun(
      auditSessionId,
      sourceRow.id,
      [column],
      [procedureColumnId],
    );
    if (!sessionRun) {
      toast.info(
        analyticsAuthoringOff
          ? runUnbuiltAnalyticsColumnAuthoringOffCopy(analyticsOwner?.kind)
          : "This test is not connected to a published workflow yet, so there is nothing to run. Attach or build one in Optro Analytics and publish it first.",
      );
      return;
    }

    try {
      // Phase 3: per-row write only — the heavy `AuditSession` mirror at
      // `['procedure-session', psId]` is no longer touched (defect 1
      // removed `useProcedureSession` from the workpaper view, so the
      // heavy entry is no longer fetched by these consumers).
      setEntry<SourceRow>(
        ["procedure-session", auditSessionId, "source-rows", sourceRow.id],
        (prev) => ({ ...prev, testsState: "processing" }),
      );
      addSessionRun(sessionRun);

      toast.info("Regenerating cell...");
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/work-paper-data/generate/v2/sample/${sourceRow.id}`,
        method: "POST",
        body: { target_column_ids: [procedureColumnId] },
      });
    } catch (error) {
      console.error("Error re-running decision:", error);
      toast.error("Error re-running cell");
      setEntry<SourceRow>(
        ["procedure-session", auditSessionId, "source-rows", sourceRow.id],
        (prev) => ({ ...prev, testsState: "ready" }),
      );
    }
  };

  const handleRerunEvalWithInstructions = async (
    instructions: string,
    persistToControl: boolean,
  ) => {
    if (!controlSession) {
      toast.error("Missing information to re-run decision");
      return;
    }
    if (checkResult?.lockedBy) {
      toast.info("Cell is locked");
      return;
    }

    let clearPending: (() => void) | undefined;
    try {
      setEntry<SourceRow>(
        ["procedure-session", auditSessionId, "source-rows", sourceRow.id],
        (prev) => ({ ...prev, testsState: "processing" }),
      );

      // Keep the cell "Running" continuously through the persist-to-control
      // phase; the parent owns the placeholder run that bridges the window
      // before the real rerun dispatches.
      clearPending = onRerunPending?.(sourceRow.id, [procedureColumnId]);

      toast.info("Regenerating cell with instructions...");
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/regenerate-with-instructions/sample/${sourceRow.id}/column/${procedureColumnId}`,
        method: "POST",
        body: {
          custom_instructions: instructions,
          // Omit the flag when unchecked: a backend without MS-2514 rejects
          // unknown fields (extra_forbidden), so plain reruns must not carry it.
          ...(persistToControl ? { persist_to_control: true } : {}),
        },
      });
    } catch (error) {
      console.error("Error re-running decision with instructions:", error);
      toast.error(
        error instanceof ApiError
          ? error.getUserMessage()
          : "Error re-running cell with instructions",
      );
      clearPending?.();
      setEntry<SourceRow>(
        ["procedure-session", auditSessionId, "source-rows", sourceRow.id],
        (prev) => ({ ...prev, testsState: "ready" }),
      );
    }
  };

  // Shared by both render branches below: the right-click actions are the
  // same whether or not the cell has a result to show, so a cell whose run
  // failed before producing anything is re-runnable from the same menu. The
  // instructions entry is the one exception, being offered only to the kinds
  // whose regeneration can carry them.
  const cellActions = (
    <>
      <ContextMenuContent className="w-52">
        <ContextMenuLabel>Cell Actions</ContextMenuLabel>
        <ContextMenuSeparator />
        <ContextMenuItem
          className="cursor-pointer flex items-center gap-2"
          onClick={handleRerunEval}
          disabled={isRerunDisabled}
        >
          <RefreshCw className="h-4 w-4" />
          Re-run cell
        </ContextMenuItem>
        {canRerunWithInstructions && (
          <ContextMenuItem
            className="cursor-pointer flex items-center gap-2"
            onClick={() => setInstructionsDialogOpen(true)}
            disabled={isRerunDisabled}
          >
            <Sparkles className="h-4 w-4" />
            Re-run with instructions
          </ContextMenuItem>
        )}
      </ContextMenuContent>

      <RegenerateWithInstructionsDialog
        open={instructionsDialogOpen}
        onOpenChange={setInstructionsDialogOpen}
        onSubmit={handleRerunEvalWithInstructions}
        title="Regenerate Cell with Instructions"
        description="Provide custom instructions to guide the regeneration of this cell."
      />
    </>
  );

  if (!checkResult) {
    // The tooltip covers either failure: a cell that says something went wrong
    // and nothing about what is the defect this surface exists to remove. The
    // label is the column channel's alone — it is what separates a settled cell
    // that produced nothing from one awaiting a Generate. A caller carrying its
    // own label names a cell the generic word misreads, so its word replaces
    // that one and the label agrees with the lead above it.
    const emptyCell = (
      <div
        data-source-row-id={sourceRow.id}
        data-column-id={column.id}
        className={`text-sm px-3 py-2 border-l border-b border-stone-300 transition-all ${
          isLast ? "border-r" : ""
        } ${isLoading ? "bg-blue-50 animate-pulse" : ""} ${hasError ? "opacity-60" : ""} ${getHighlightCellClassName(
          isHighlighted,
          isHighlightFading,
          highlightEdges,
        )}`}
        style={{
          minWidth: width ?? DEFAULT_COLUMN_WIDTH,
          flex: `1 0 ${width ?? DEFAULT_COLUMN_WIDTH}px`,
          boxSizing: "border-box",
        }}
      >
        <div
          className={`flex items-center gap-1.5 ${isLoading ? "gradient-sweep" : ""}`}
        >
          {hasError && (
            <AlertCircle
              className={`h-3.5 w-3.5 shrink-0 ${
                isColumnScopedFailure ? "text-amber-600" : "text-red-600"
              }`}
            />
          )}
          <div className={isColumnScopedFailure ? "text-amber-700" : ""}>
            {isLoading
              ? "Running..."
              : isColumnScopedFailure
                ? (errorLabel ?? "Could Not Generate")
                : ""}
          </div>
        </div>
      </div>
    );

    // Analytics cells keep the placeholder they had: no right-click actions on
    // a cell with no verdict.
    if (column.analyticsColumn) {
      if (!hasError) return emptyCell;

      return (
        <Tooltip>
          <TooltipTrigger asChild>{emptyCell}</TooltipTrigger>
          <CellErrorTooltipContent
            isTimeout={isTimeout}
            errorMessage={errorMessage}
            errorLead={computedErrorLead}
          />
        </Tooltip>
      );
    }

    // No result to select or lock, so no click handler and no padlock — but
    // the cell still owns its right-click actions, otherwise a run that died
    // before writing this cell leaves it with no way to be re-run short of
    // regenerating the whole row.
    const emptyCellTrigger = (
      <ContextMenuTrigger asChild>{emptyCell}</ContextMenuTrigger>
    );

    return (
      <ContextMenu>
        {hasError ? (
          <Tooltip>
            <TooltipTrigger asChild>{emptyCellTrigger}</TooltipTrigger>
            <CellErrorTooltipContent
              isTimeout={isTimeout}
              errorMessage={errorMessage}
              errorLead={computedErrorLead}
            />
          </Tooltip>
        ) : (
          emptyCellTrigger
        )}
        {cellActions}
      </ContextMenu>
    );
  }

  const handleClick = () => {
    if (isLoading) {
      return;
    }
    onCellSelection({
      sourceRowId: sourceRow.id,
      checkResultId: checkResult.id,
      documentId: undefined,
      attributeId: undefined,
      supportColumnId: undefined,
      value: undefined,
      evalColumnId: checkResult.evalColumnId ?? undefined,
    });

    // Delay scroll to allow right panel to open and layout to adjust
    setTimeout(() => {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        const scrollContainer = cellRef.current.closest(".overflow-x-scroll");

        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          // Check if cell is visible within the scroll container
          const isVisibleInContainer =
            rect.left >= containerRect.left &&
            rect.right <= containerRect.right;

          // Only scroll if the cell is not visible in the container
          if (!isVisibleInContainer) {
            cellRef.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "start",
            });
          }
        }
      }
    }, 300);
  };

  // Derived below the verdict-less return, so there is no cell without a result
  // to give a status to. The one such mapping this badge could carry is
  // "no verdict" → "No Issues Found", which reports an ungenerated cell clean.
  const displayStatus = getIssueDisplayStatus(checkResult.issues);

  // Determine styling classes based on display status
  const getStatusClasses = () => {
    switch (displayStatus) {
      case "na":
        return "bg-gray-50 hover:bg-gray-100";
      case "requires_attention":
        return "bg-orange-50 hover:bg-orange-100";
      case "issues_found":
        return "bg-red-50 hover:bg-red-100";
      case "no_issues":
        return "bg-green-50 hover:bg-green-100";
      default:
        return "bg-green-50 hover:bg-green-100";
    }
  };

  // Determine text and styling for display
  const getStatusDisplay = () => {
    switch (displayStatus) {
      case "na":
        return <div className="text-gray-700">N/A</div>;
      case "requires_attention":
        return <div className="text-orange-700">Requires Attention</div>;
      case "issues_found":
        return <div className="text-red-700">Issues Found</div>;
      case "no_issues":
        return <div>No Issues Found</div>;
      default:
        return <div>No Issues Found</div>;
    }
  };

  return (
    <ContextMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <ContextMenuTrigger asChild>
            <div
              ref={cellRef}
              data-source-row-id={sourceRow.id}
              data-column-id={column.id}
              data-selected={isSelected ? "true" : undefined}
              className={`group relative group/cell text-sm px-3 py-2 border-l border-b border-stone-300 cursor-pointer transition-all ${getStatusClasses()} ${
                isLast ? "border-r" : ""
              } ${
                isSelected ? "border border-stone-500" : ""
              } ${isLoading ? "!bg-blue-50 !animate-pulse" : ""} ${
                isStale || hasError ? "opacity-60" : ""
              } ${getHighlightCellClassName(isHighlighted, isHighlightFading, highlightEdges)}`}
              style={{
                minWidth: width ?? DEFAULT_COLUMN_WIDTH,
                flex: `1 0 ${width ?? DEFAULT_COLUMN_WIDTH}px`,
                boxSizing: "border-box",
              }}
              onClick={handleClick}
              role="button"
            >
              {/*
                Keep the prior badge visible during `isLoading` (with a
                shimmer overlay) instead of replacing it with a bare
                "Running..." text node — eliminates the blank frame between
                load and final result when a cell already has a prior
                checkResult. The outer cell still gets the loading
                affordance via `!bg-blue-50 !animate-pulse` above.
              */}
              <div
                className={`flex items-center gap-1.5 ${isLoading ? "gradient-sweep" : ""}`}
              >
                {hasError && (
                  <AlertCircle
                    className={`h-3.5 w-3.5 shrink-0 ${
                      isColumnScopedFailure ? "text-amber-600" : "text-red-600"
                    }`}
                  />
                )}
                {isStale && !hasError && (
                  <AlertTriangle className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                )}
                {getStatusDisplay()}
                {conclusionCard}
              </div>
              {onToggleLock && (
                <CellLockButton
                  lockedBy={checkResult.lockedBy}
                  disabled={controlSession?.status === "complete"}
                  onToggle={onToggleLock}
                />
              )}
            </div>
          </ContextMenuTrigger>
        </TooltipTrigger>
        {hasError && (
          <CellErrorTooltipContent
            isTimeout={isTimeout}
            errorMessage={errorMessage}
            errorLead={computedErrorLead}
          />
        )}
        {isStale && !hasError && (
          <TooltipContent className="max-w-xs break-words">
            <p>This result is stale</p>
          </TooltipContent>
        )}
      </Tooltip>
      {cellActions}
    </ContextMenu>
  );
};
